import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import moment from "moment";
import styles from "./index.module.scss";
import { useBranding } from "../../Context/BrandingContext";

// Verified link map — every URL points at an actual route on the site.
const LINKS = [
  { label: "Shop All",      to: "/product/all" },
  { label: "Sell Gold",     to: "/sell-gold/get-a-quote" },
  { label: "Design & CAD",  to: "/design-and-cad" },
  { label: "Our Story",     to: "/about" },
  { label: "Craftsmanship", to: "/craftsmanship" },
  { label: "Contact",       to: "/contact" },
  { label: "FAQ",           to: "/faq" },
  { label: "Returns",       to: "/refund" },
  { label: "Privacy",       to: "/privacy" },
];

// Single-fill brand glyphs that inherit the parent's `color` so they
// flip to gold on hover via the SCSS .socialIcon rule.
const SOCIAL = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/thediamondcounter",
    path: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="17.5" cy="6.5" r="1.1" fill="currentColor" />
      </>
    ),
  },
  {
    // LinkedIn URL not set yet — icon renders but doesn't navigate.
    label: "LinkedIn",
    href: "",
    path: (
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor" />
    ),
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/447793219535",
    path: (
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor" />
    ),
  },
];

// Brand-coloured card glyphs supplied as PNGs, downscaled to 96×96 and
// stored in /public/payment/. They sit on white tiles so the brand
// colours read at a glance against the black footer.
const PAYMENT_ICONS = [
  { label: "Visa",             src: "/payment/visa.png" },
  { label: "Mastercard",       src: "/payment/mastercard.png" },
  { label: "American Express", src: "/payment/amex.png" },
  { label: "Apple Pay",        src: "/payment/apple-pay.png" },
  { label: "Google Pay",       src: "/payment/google-pay.png" },
];

function Footer() {
  const navigate = useNavigate();
  const [year, setYear] = useState();
  const branding = useBranding();

  useEffect(() => {
    setYear(moment(new Date()).format("YYYY"));
  }, []);

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <a href="/" className={styles.logoLink} aria-label={`${branding.name} — home`}>
          <img src={branding.footerLogo} alt={branding.name} className={styles.logoImg} />
        </a>

        <div className={styles.goldSeparator} aria-hidden="true" />

        {/* Single-row link nav */}
        <nav className={styles.nav} aria-label="Footer navigation">
          {LINKS.map((l) => (
            <a
              key={l.to}
              href={l.to}
              onClick={(e) => { e.preventDefault(); navigate(l.to); }}
              className={styles.navLink}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className={styles.goldSeparator} aria-hidden="true" />

        {/* Social icons */}
        <div className={styles.social}>
          {SOCIAL.map((s) => (
            s.href ? (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer noopener" aria-label={s.label} className={styles.socialIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24">{s.path}</svg>
              </a>
            ) : (
              <span key={s.label} aria-label={`${s.label} (coming soon)`} className={styles.socialIcon} style={{ cursor: "default", opacity: 0.4 }}>
                <svg width="20" height="20" viewBox="0 0 24 24">{s.path}</svg>
              </span>
            )
          ))}
        </div>

        {/* Payment icons */}
        <div className={styles.payments}>
          {PAYMENT_ICONS.map((p) => (
            <img key={p.label} src={p.src} alt={p.label} title={p.label} className={styles.paymentIcon} />
          ))}
        </div>

        {/* Staff Mail sits on the copyright line rather than in the nav columns:
            it is a staff utility, and a retailer placing an order has no use for
            a webmail login. Points at Hostinger, where both this mailbox and
            Xclusive's now live. */}
        <p className={styles.copyright}>
          © {year} {branding.name} · All rights reserved
          {/* Separator and link kept in one non-wrapping span: on a 390px phone
              the line breaks between "Staff" and "Mail", splitting the underline
              across two rows. */}
          <span className={styles.staffWrap}>
            {" · "}
            <a
              href="https://mail.hostinger.com/"
              target="_blank"
              rel="noreferrer noopener"
              className={styles.staffLink}
            >
              Staff Mail
            </a>
          </span>
        </p>
      </div>
    </footer>
  );
}

export default Footer;
